import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelDeviceCommand,
  fetchDeviceCategories,
  fetchDevices,
  queueDeviceCommand,
  renameDevice,
} from './api';
import { ACTION_FILTER_QUEUED, DEVICE_COPY, DEVICE_POLICY, type CommandType } from './constants';
import type { Device, DevicePage } from './schemas';
import type { DeviceFilters } from './types';

export const deviceKeys = {
  all: ['devices'] as const,
  list: (input: DeviceListInput) => [...deviceKeys.all, 'list', input] as const,
  categories: () => [...deviceKeys.all, 'categories'] as const,
  detail: (id: string) => [...deviceKeys.all, 'detail', id] as const,
};

export type DeviceListInput = {
  /** Category id from `meta/categories`, or `null` for the "All" tab. */
  categoryId: string | null;
  search: string;
  filters: DeviceFilters;
};

/**
 * The device grid (US-112439 §12): cursor-paginated batches loaded progressively, no pagination
 * controls. Every filter is a query param — the client never filters or re-sorts a page.
 *
 * `refetchOnWindowFocus` is switched **off** against the app-wide default, and `staleTime` is
 * `Infinity`, because §14 is explicit that the list must not update on its own: "the new data
 * appears only after I manually refresh the page". A focus refetch would silently violate that.
 */
export function useDeviceList(input: DeviceListInput) {
  return useInfiniteQuery({
    queryKey: deviceKeys.list(input),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchDevices({
        limit: DEVICE_POLICY.PAGE_SIZE,
        cursor: pageParam,
        search: input.search,
        categoryId: input.categoryId,
        deviceStatus: input.filters.status,
        subscriptionStatus: input.filters.subscriptionStatus,
        queuedOnly: input.filters.action.includes(ACTION_FILTER_QUEUED),
      }),
    // A truthy `hasNextPage` with no cursor would re-request the same page forever, so both are
    // required before another batch is offered.
    getNextPageParam: (lastPage: DevicePage) =>
      lastPage.hasNextPage && lastPage.nextCursor ? lastPage.nextCursor : null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * The grower's device categories, which are the listing's quick-filter tabs. Per account and
 * effectively static for a session, so they are cached rather than re-read.
 */
export function useDeviceCategories() {
  return useQuery({
    queryKey: deviceKeys.categories(),
    queryFn: fetchDeviceCategories,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Flatten the loaded batches into one array for the grid. */
export function flattenDevicePages(pages: DevicePage[] | undefined): Device[] {
  return (pages ?? []).flatMap((page) => page.devices);
}

/**
 * Queue a Turn On / Turn Off command.
 *
 * Invalidating the whole `devices` key rather than patching one card is deliberate: queuing
 * changes both the row's `queuedAction` and — once executed — its server-computed
 * `effectiveStatus`, `powerIcon` and `cardState`. Re-reading is the only way to stay consistent
 * with a server that owns all four.
 *
 * There is no command-status channel on this API (no push, webhook or poll endpoint), so
 * completion is only observed on the next read — which §14 says is a manual refresh anyway.
 */
export function useQueueCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: queueDeviceCommand,
    // The confirmation names the direction, so the copy is resolved from the mutation's own
    // variables rather than pinned to a string — the call sites no longer toast for themselves.
    meta: {
      successMessage: ({ type }: { deviceId: string; type: CommandType }) =>
        DEVICE_COPY.QUEUE_SUCCESS[type],
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceKeys.all }),
  });
}

/** Cancel the one queued command for a device (US-112437 §9). Needs the command's own id. */
export function useCancelCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelDeviceCommand,
    meta: { successMessage: 'Queued command cancelled' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceKeys.all }),
  });
}

/** Rename (or, with `null`, un-name) a device. Uniqueness is enforced server-side. */
export function useRenameDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: renameDevice,
    meta: { successMessage: 'Device name updated successfully.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceKeys.all }),
  });
}
